/**
 * MLP chapter tabs — page-level navigation controller.
 *
 * Shared by `sections/mlp-hero.liquid` (the chapters intro nav) and
 * `sections/mlp-tabs-sticky.liquid` (the sticky bar). Both emit chapter links
 * marked `[data-mlp-chapter-link]`; this controller wires every such link on the
 * page to one filtering model, so the two navs stay in sync. Each link is one of:
 *   - In-page hash (e.g. `#men`, `#all`, or a bare `#`) — filters page content
 *     without a reload. Panels marked `[data-mlp-chapter-content="<keys>"]` fade
 *     in/out so only the matching panel(s) stay visible; a panel may list several
 *     space/comma-separated keys to belong to more than one chapter. `#`, `#all`
 *     (or an empty hash) show everything.
 *   - Cross-page URL — navigates normally; the theme's View Transitions
 *     (assets/view-transitions.js) provide the fade-in/out.
 *
 * The active tab is derived from the current URL/hash on load and kept in sync
 * with the browser back/forward buttons. Links stay real `<a href>` elements, so
 * the components degrade to plain navigation when JS is unavailable.
 *
 * The sticky bar's pinning is pure CSS (`position: sticky`), but its reveal is
 * handled here (see initStickyReveal): the bar is hidden on load and slides in
 * only once the Chapters-variant MLP hero has scrolled past — so it is never
 * visible until it pins to the top, and only when a Chapters intro exists.
 *
 * Loaded with `type="module"`, so this file is deferred and de-duplicated even
 * when both sections request it.
 */

(function () {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------------------------------------------------------------------------
  // Sticky-bar reveal (progressive enhancement).
  //
  // The "MLP tabs sticky" bar restates the Chapters intro nav. On load it must be
  // hidden (the section CSS hides it by default); it slides in only once the page
  // has scrolled past the Chapters-variant MLP hero, so the bar reaches the top of
  // the viewport and pins. We watch that hero with an IntersectionObserver and
  // toggle `.is-pinned` on the bar's section the moment the hero has scrolled fully
  // above the viewport — the same boundary at which the (adjacent) bar pins, on
  // both mobile and desktop. Mirrors the reveal-on-scroll pattern in
  // assets/sticky-add-to-cart.js. With no Chapters hero on the page nothing is
  // observed, so the bar stays hidden — it only ever appears for a Chapters intro.
  const initStickyReveal = () => {
    if (!('IntersectionObserver' in window)) return;

    const stickySections = Array.from(document.querySelectorAll('.section-mlp-tabs-sticky'));
    if (!stickySections.length) return;

    const chapterHeroes = Array.from(document.querySelectorAll('.mlp-hero--intro-chapters'));
    if (!chapterHeroes.length) return; // No Chapters intro → leave the bar hidden.

    // The Chapters hero that immediately precedes a given sticky section.
    const heroBefore = (section) =>
      chapterHeroes
        .filter((hero) => hero.compareDocumentPosition(section) & Node.DOCUMENT_POSITION_FOLLOWING)
        .pop() || null;

    stickySections.forEach((section) => {
      const hero = heroBefore(section);
      if (!hero) return; // Not paired with a preceding Chapters hero → stays hidden.

      const observer = new IntersectionObserver(([entry]) => {
        if (!entry) return;
        // Pinned once the hero has scrolled fully above the top of the viewport.
        const pinned = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        section.classList.toggle('is-pinned', pinned);
      });
      observer.observe(hero);
    });
  };

  initStickyReveal();

  /** Resolve a link's href against the current location (null if malformed). */
  const urlOf = (link) => {
    try {
      return new URL(link.getAttribute('href') || '', window.location.href);
    } catch (_e) {
      return null;
    }
  };

  /** True when a link is an in-page hash tab (filters) rather than a navigation. */
  const isHashLink = (link) => {
    const raw = (link.getAttribute('href') || '').trim();
    if (raw.charAt(0) === '#') return true; // #, #all, #men
    const url = urlOf(link);
    return !!url && url.hash !== '' && url.pathname === window.location.pathname;
  };

  /** Normalise a hash to a filter key: lowercase, no '#'; '' and 'all' both mean "show all". */
  const normaliseKey = (hash) => {
    const key = decodeURIComponent((hash || '').replace(/^#/, '')).toLowerCase();
    return key === 'all' ? '' : key;
  };

  /** The filter key a hash link targets. */
  const keyOf = (link) => normaliseKey(urlOf(link)?.hash || '');

  /** A stable identity for a link's target so links pointing to the same place —
   *  the in-hero nav and the sticky bar — activate together. */
  const sigOf = (link) =>
    isHashLink(link) ? 'hash:' + keyOf(link) : 'path:' + (urlOf(link)?.pathname || '');

  // One controller for the whole page: every chapter link, wherever it lives.
  const links = Array.from(document.querySelectorAll('[data-mlp-chapter-link]'));
  if (!links.length) return;

  // Center an active tab inside a horizontal scroller (the sticky bar) without
  // nudging the page scroll. Geometry via rects so it is independent of the tab's
  // offsetParent. No-op if the scroller doesn't overflow.
  const scrollTabIntoView = (link) => {
    const scroller = link.closest('[data-mlp-tabs-scroll]');
    if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;
    const linkRect = link.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const linkLeftInContent = linkRect.left - scrollerRect.left + scroller.scrollLeft;
    const target = linkLeftInContent - (scroller.clientWidth - linkRect.width) / 2;
    const max = scroller.scrollWidth - scroller.clientWidth;
    scroller.scrollTo({
      left: Math.max(0, Math.min(target, max)),
      behavior: prefersReduced ? 'auto' : 'smooth',
    });
  };

  // Highlight every link that shares the active link's target, so the in-hero
  // nav and the sticky bar stay in sync.
  const setActive = (activeLink) => {
    const activeSig = activeLink ? sigOf(activeLink) : null;
    links.forEach((link) => {
      const active = activeSig !== null && sigOf(link) === activeSig;
      link.classList.toggle('is-active', active);
      if (active) {
        link.setAttribute('aria-current', 'true');
        scrollTabIntoView(link);
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  const reveal = (panel, immediate) => {
    panel.hidden = false;
    if (immediate || prefersReduced) {
      panel.classList.remove('mlp-chapter-content--hidden');
      return;
    }
    // Force a reflow so the opacity transition runs from the hidden state.
    void panel.offsetWidth;
    panel.classList.remove('mlp-chapter-content--hidden');
  };

  const hide = (panel, immediate) => {
    if (panel.hidden) return;
    panel.classList.add('mlp-chapter-content--hidden');
    if (immediate || prefersReduced) {
      panel.hidden = true;
      return;
    }
    const onEnd = (event) => {
      if (event.propertyName !== 'opacity') return;
      panel.hidden = true;
      panel.removeEventListener('transitionend', onEnd);
    };
    panel.addEventListener('transitionend', onEnd);
  };

  // Filter page content panels to `key` ('' shows all). No-op if no panels exist.
  const applyFilter = (key, immediate) => {
    document.querySelectorAll('[data-mlp-chapter-content]').forEach((panel) => {
      // A panel may list several chapter keys (space/comma separated), so it can
      // belong to more than one chapter — e.g. `data-mlp-chapter-content="jason anne"`.
      const panelKeys = (panel.getAttribute('data-mlp-chapter-content') || '')
        .toLowerCase()
        .split(/[\s,]+/)
        .filter(Boolean);
      const show = key === '' || panelKeys.includes(key);
      if (show) reveal(panel, immediate);
      else hide(panel, immediate);
    });
  };

  // The link that matches the current location, by priority:
  //   1. a hash tab matching the current #hash
  //   2. a cross-page tab whose path is the current page
  //   3. the "show all" hash tab (only when there's no #hash)
  //   4. the first link
  const linkForLocation = () => {
    const hash = window.location.hash;
    if (hash) {
      const key = normaliseKey(hash);
      const byHash = links.find((link) => isHashLink(link) && keyOf(link) === key);
      if (byHash) return byHash;
    }
    const byPath = links.find((link) => {
      const url = urlOf(link);
      return url && !isHashLink(link) && url.pathname === window.location.pathname;
    });
    if (byPath) return byPath;
    if (!hash) {
      const allTab = links.find((link) => isHashLink(link) && keyOf(link) === '');
      if (allTab) return allTab;
    }
    return links[0];
  };

  const sync = (immediate) => {
    const active = linkForLocation();
    setActive(active);
    if (isHashLink(active)) applyFilter(keyOf(active), immediate);
  };

  links.forEach((link) => {
    link.addEventListener('click', (event) => {
      if (!isHashLink(link)) return; // cross-page → let the browser navigate (view transition)
      event.preventDefault();
      const url = urlOf(link);
      const nextHash = url ? url.hash : '';
      if (window.location.hash !== nextHash) {
        history.pushState(null, '', nextHash || window.location.pathname + window.location.search);
      }
      setActive(link);
      applyFilter(keyOf(link));
    });
  });

  window.addEventListener('hashchange', () => sync(false));
  window.addEventListener('popstate', () => sync(false));

  sync(true); // initial state from the URL, without a fade-out flash
})();
